import { useCallback, useEffect, useState } from 'react';

import Icon from 'components/global/Icon';
import Tooltip from 'components/global/Tooltip';

import useLocalStorage from 'hooks/useLocalStorage';

import { faBarcode, faCheck, faPlus, faScannerGun } from '@fortawesome/pro-solid-svg-icons';

import Button from './Button';
import StringInput from './StringInput';
import * as S from './styled/BarcodeInput.styled';

/**
 * @param {Object} props
 * @param {"scanner" | "validation"} [props.variant = "scanner" ] - If the variant is "scanner", the input will have a button to add the barcode to the list. If the variant is "validation", the input will have a checkmark icon if the barcode is valid.
 * @param {boolean} [props.validated] - If the variant is "validation", this prop will determine if the checkmark icon is shown.
 * @param {function} props.onChange - The function that will be called when the barcode enters the input.
 * @param {function} [props.onEveryChange] - The function that will be called when the barcode input changed.
 * @param {boolean} [props.loading = false] - If true, the button will show a loading spinner.
 * @param {boolean} [props.autoRemove = true] - If true, the input will be cleared after the barcode is added to the list.
 */
const BarcodeInput = ({
	variant = 'scanner',
	validated,
	onChange,
	onEveryChange,
	autoRemove = true,
	loading = false
}) => {
	const [value, setValue] = useState('');
	const [inputRef, setInputRef] = useState(null);
	const [autoFocusMode, setAutoFocusMode] = useLocalStorage('barcodeInputAutoFocusMode', false);

	const handleButtonClick = useCallback(() => {
		onChange && onChange(inputRef.value);
		(autoFocusMode || autoRemove) && setValue('');
		onEveryChange && onEveryChange('');
	}, [inputRef, setValue, onChange, onEveryChange, autoFocusMode, autoRemove]);

	const handleKeyDown = useCallback(
		(event) => {
			event.stopPropagation();
			event.key === 'Enter' && handleButtonClick();
		},
		[handleButtonClick]
	);

	const focusInput = useCallback(() => {
		[...document.querySelectorAll('#barcode-input')].pop()?.focus();
	}, []);

	const handleOnBlur = useCallback(() => {
		autoFocusMode &&
			setTimeout(() => {
				focusInput();
			}, 200);
	}, [inputRef, autoFocusMode]);

	const handleValueChange = useCallback(
		(value) => {
			setValue(value);
			onEveryChange && onEveryChange(value);
		},
		[setValue, onEveryChange]
	);

	useEffect(() => {
		autoFocusMode && inputRef?.addEventListener('focusout', handleOnBlur);

		return () => {
			inputRef?.removeEventListener('focusout', handleOnBlur);
		};
	}, [inputRef, autoFocusMode, handleOnBlur]);

	useEffect(() => {
		inputRef?.addEventListener('keydown', handleKeyDown);

		return () => {
			inputRef?.removeEventListener('keydown', handleKeyDown);
		};
	}, [inputRef, handleKeyDown]);

	const handleAutoFocusChange = useCallback(() => {
		if (!autoFocusMode) {
			focusInput();
		}
		setValue('');
		setAutoFocusMode(!autoFocusMode);
	}, [autoFocusMode, setAutoFocusMode, setValue, focusInput]);

	return (
		<S.Container>
			<Tooltip title="autoFocusBarcode" placement="top">
				<Button
					icon={faScannerGun}
					onClick={handleAutoFocusChange}
					className={autoFocusMode ? 'green-button' : ''}
				/>
			</Tooltip>

			<S.InputWrapper variant={variant}>
				<Icon icon={faBarcode} className="left-icon" color="var(--black)" />
				<StringInput
					id="barcode-input"
					autoFocus
					ref={setInputRef}
					value={value}
					setValue={handleValueChange}
					onBlur={handleOnBlur}
				/>

				{variant === 'scanner' && (
					<S.AddButton
						icon={faPlus}
						size="small"
						disabled={!value}
						loading={loading}
						label="Add"
						onClick={handleButtonClick}
					/>
				)}
				{variant === 'validation' && validated && (
					<Icon icon={faCheck} color="var(--green)" className="right-icon" />
				)}
			</S.InputWrapper>
		</S.Container>
	);
};

export default BarcodeInput;
